<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { useI18n } from "vue-i18n"
import type { PvEtudeEcoGeneralInfoForm } from "@modules/pv/types/pv-etude-eco-form.types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            sectionTitle: "Informations générales relatives à l'installation",
            placeholder: "----",
            prix_installation:
                "Prix HT de l’installation (incluant l’onduleur)",
            duree_vie_installation: "Durée de vie de l’installation",
            prix_ondulateur: "Prix HT de l’onduleur",
            duree_vie_ondulateur: "Durée de vie de l’onduleur",
            taux_inflation: "Taux d’inflation",
            taux_actualisation: "Taux d’actualisation",
            prix_maintenance: "Coût annuel de la maintenance",
            hint_taux_inflation_short: "Hausse moyenne annuelle des prix",
            hint_taux_inflation_full:
                "Exprimé en %, le taux d’inflation mesure l’augmentation annuelle moyenne des prix. Il est utilisé pour prendre en compte l’évolution des prix de l’énergie et des coûts liés à la maintenance.",
            hint_taux_actualisation_short:
                "Actualisation des flux futurstoastEcoCreated",
            hint_taux_actualisation_full:
                "Exprimé en %, le taux d’actualisation mesure la préférence pour le présent. Il est utilisé pour pondérer les revenus futurs par rapport aux revenus présents dans le calcul des flux de trésorerie.",
            hint_prix_maintenance_short:
                "Maintenance et réparations du parc PV.",
            hint_prix_maintenance_full:
                "Représente les coûts alloués à la maintenance et aux réparations des équipements photovoltaïques tout au long de leur durée de vie.",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
withDefaults(
    defineProps<{
        /** Erreurs vee-validate (clés = noms de champs du modèle). */
        fieldErrors?: Record<string, string | undefined>
    }>(),
    {
        fieldErrors: () => ({}),
    }
)

const model = defineModel<PvEtudeEcoGeneralInfoForm>({ required: true })
</script>

<template>
    <div class="pv-etude-eco-form-general-info flex flex-col gap-4">
        <h3 class="text-base font-semibold text-primary-900">
            {{ t("sectionTitle") }}
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <BaseInputWrapper
                input-id="pv-eco-prix-installation"
                :label="t('prix_installation')"
                suffix="€"
                :error="fieldErrors?.prix_installation"
            >
                <BaseInputNumber
                    id="pv-eco-prix-installation"
                    v-model="model.prix_installation"
                    :min-fraction-digits="0"
                    :max-fraction-digits="4"
                    :step="0.01"
                    :placeholder="t('placeholder')"
                    class="w-full"
                    :invalid="Boolean(fieldErrors?.prix_installation)"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                input-id="pv-eco-duree-installation"
                :label="t('duree_vie_installation')"
                suffix="an(s)"
                :error="fieldErrors?.duree_vie_installation"
            >
                <BaseInputNumber
                    id="pv-eco-duree-installation"
                    v-model="model.duree_vie_installation"
                    :min="0"
                    :max-fraction-digits="0"
                    class="w-full"
                    :invalid="Boolean(fieldErrors?.duree_vie_installation)"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                input-id="pv-eco-prix-onduleur"
                :label="t('prix_ondulateur')"
                suffix="€"
                :error="fieldErrors?.prix_ondulateur"
            >
                <BaseInputNumber
                    id="pv-eco-prix-onduleur"
                    v-model="model.prix_ondulateur"
                    :min-fraction-digits="0"
                    :max-fraction-digits="4"
                    :step="0.01"
                    :placeholder="t('placeholder')"
                    class="w-full"
                    :invalid="Boolean(fieldErrors?.prix_ondulateur)"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                input-id="pv-eco-duree-onduleur"
                :label="t('duree_vie_ondulateur')"
                suffix="an(s)"
                :error="fieldErrors?.duree_vie_ondulateur"
            >
                <BaseInputNumber
                    id="pv-eco-duree-onduleur"
                    v-model="model.duree_vie_ondulateur"
                    :min="0"
                    :max-fraction-digits="0"
                    class="w-full"
                    :invalid="Boolean(fieldErrors?.duree_vie_ondulateur)"
                />
            </BaseInputWrapper>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            <BaseInputWrapper
                input-id="pv-eco-taux-inflation"
                :label="t('taux_inflation')"
                suffix="%"
                :error="fieldErrors?.taux_inflation"
            >
                <template #hint>
                    <span>{{ t("hint_taux_inflation_short") }}</span>
                    <BaseTooltipMessage
                        :content="t('hint_taux_inflation_full')"
                        size="small"
                    />
                </template>
                <BaseInputNumber
                    id="pv-eco-taux-inflation"
                    v-model="model.taux_inflation"
                    :step="0.01"
                    :max-fraction-digits="4"
                    class="w-full"
                    :invalid="Boolean(fieldErrors?.taux_inflation)"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                input-id="pv-eco-taux-actu"
                :label="t('taux_actualisation')"
                suffix="%"
                :error="fieldErrors?.taux_actualisation"
            >
                <template #hint>
                    <span>{{ t("hint_taux_actualisation_short") }}</span>
                    <BaseTooltipMessage
                        :content="t('hint_taux_actualisation_full')"
                        size="small"
                    />
                </template>
                <BaseInputNumber
                    id="pv-eco-taux-actu"
                    v-model="model.taux_actualisation"
                    :step="0.01"
                    :max-fraction-digits="4"
                    class="w-full"
                    :invalid="Boolean(fieldErrors?.taux_actualisation)"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                input-id="pv-eco-prix-maintenance"
                :label="t('prix_maintenance')"
                suffix="€"
                :error="fieldErrors?.prix_maintenance"
            >
                <template #hint>
                    <span>{{ t("hint_prix_maintenance_short") }}</span>
                    <BaseTooltipMessage
                        :content="t('hint_prix_maintenance_full')"
                        size="small"
                    />
                </template>
                <BaseInputNumber
                    id="pv-eco-prix-maintenance"
                    v-model="model.prix_maintenance"
                    :min-fraction-digits="0"
                    :max-fraction-digits="4"
                    :step="0.01"
                    :placeholder="t('placeholder')"
                    class="w-full"
                    :invalid="Boolean(fieldErrors?.prix_maintenance)"
                />
            </BaseInputWrapper>
        </div>
    </div>
</template>
